import type {ChatStatusItem, PageChangeOperation, PageChangeSetStatusItem} from '../chat-status';
import {isPageChangeSetStatusItem} from '../chat-status';

export type PageChangeSetLabels = Record<string, string>;

/** What a button on a card asks the server to do. Resolves to an error text to show, or null when it worked. */
export type PageChangeSetActions = {
    apply: (set: PageChangeSetStatusItem) => Promise<string | null>;
    skip: (set: PageChangeSetStatusItem, operationId: string) => Promise<string | null>;
    cancel: (set: PageChangeSetStatusItem) => Promise<string | null>;
};

type Options = {
    labels: PageChangeSetLabels;
    actions: PageChangeSetActions;
    currentPageId: () => string;
    isRunning: () => boolean;
    close: string;
};

/**
 * The summary cards of change sets in the chat: what is staged on which page with its content, and the
 * buttons that apply, cancel or skip it. A finished set stays as a record of what was applied until it is
 * dismissed.
 *
 * Cards are drawn from the server's view of the set every time it changes. The buttons never change a card
 * themselves, the answer from the server does.
 */
export class ChangeSetCards {
    private signature = '';
    private readonly dismissed = new Set<string>();
    /** Set id to the error the last button press on that card came back with, and the set version it was about. */
    private readonly errors = new Map<string, {text: string; version: string}>();
    /** Set id to the version last announced, so only real changes are read out. */
    private readonly announced = new Map<string, string>();
    private readonly announcer: HTMLElement;
    private busy = false;
    private rendered = false;
    /** The control pressed last. Browsers drop focus from a button once it is disabled, so it is kept here. */
    private focusAfterAction: string | undefined;
    private statusItems: ChatStatusItem[] = [];

    constructor(private readonly element: HTMLElement, private readonly options: Options) {
        this.element.className = 'howspace-ai-change-sets';
        this.element.hidden = true;
        // The cards are redrawn whole, so they are not a live region themselves. One sentence per change is.
        this.announcer = document.createElement('div');
        this.announcer.className = 'sr-only';
        this.announcer.setAttribute('aria-live', 'polite');
        this.element.after(this.announcer);
    }

    render(statusItems: ChatStatusItem[]): void {
        this.statusItems = statusItems;
        const pageId = this.options.currentPageId();
        // Changes waiting for approval are shown, and applied, only on the page they were staged for. Leaving the
        // page hides them, and coming back shows them again. A finished set stays as the record of what changed.
        const sets = statusItems
            .filter(isPageChangeSetStatusItem)
            .filter(set => !this.dismissed.has(set.id))
            .filter(set => (set.status !== 'staged' && set.status !== 'applying') || set.pageId === pageId);
        sets.forEach(set => {
            const error = this.errors.get(set.id);
            if (error && error.version !== versionOf(set)) {
                this.errors.delete(set.id);
            }
        });
        const signature = JSON.stringify([
            pageId,
            this.busy,
            [...this.errors],
            sets.map(set => [set.id, set.status, set.revision, set.pageTitle, set.operations]),
        ]);
        if (signature !== this.signature) {
            this.signature = signature;
            const focusKey = (document.activeElement as HTMLElement | null)?.dataset?.focusKey ?? this.focusAfterAction;
            this.element.replaceChildren(...sets.map(set => this.renderCard(set)));
            this.element.hidden = sets.length === 0;
            this.syncDisabled();
            this.restoreFocus(focusKey);
            this.announce(sets);
            if (!this.busy) {
                this.focusAfterAction = undefined;
            }
        }
        this.syncDisabled();
    }

    refresh(): void {
        this.render(this.statusItems);
    }

    /**
     * Running replies come and go without the cards changing, so only the buttons follow them.
     */
    private syncDisabled(): void {
        const disabled = this.busy || this.options.isRunning();
        this.element.querySelectorAll<HTMLButtonElement>('button[data-action]').forEach(button => {
            button.disabled = disabled;
        });
    }

    /**
     * A redraw replaces the focused button. Focus goes to the same control when it is still there, otherwise to
     * the first control of the same card, or its heading.
     */
    private restoreFocus(focusKey: string | undefined): void {
        if (!focusKey) {
            return;
        }
        const same = this.element.querySelector<HTMLElement>(`[data-focus-key="${CSS.escape(focusKey)}"]`);
        if (same) {
            same.focus();
            return;
        }
        const setId = focusKey.split(':')[1] || '';
        const card = this.element.querySelector<HTMLElement>(`[data-set-id="${CSS.escape(setId)}"]`);
        const fallback = card?.querySelector<HTMLElement>('button[data-action]:not(:disabled)')
            ?? card?.querySelector<HTMLElement>('.howspace-ai-change-set-heading');
        fallback?.focus();
    }

    private announce(sets: PageChangeSetStatusItem[]): void {
        const changed = sets.filter(set => this.announced.get(set.id) !== versionOf(set));
        sets.forEach(set => this.announced.set(set.id, versionOf(set)));
        if (this.rendered && changed.length > 0) {
            this.announcer.textContent = changed.map(set => this.headingText(set)).join('. ');
        }
        this.rendered = true;
    }

    private renderCard(set: PageChangeSetStatusItem): HTMLElement {
        const card = document.createElement('section');
        card.className = `howspace-ai-change-set is-${set.status}`;
        card.dataset.setId = set.id;
        const heading = document.createElement('h3');
        heading.className = 'howspace-ai-change-set-heading';
        heading.id = `howspace-ai-change-set-${set.id}`;
        heading.tabIndex = -1;
        heading.textContent = this.headingText(set);
        card.setAttribute('aria-labelledby', heading.id);

        const headingRow = document.createElement('div');
        headingRow.className = 'howspace-ai-change-set-heading-row';
        headingRow.append(heading);
        if (set.status !== 'staged' && set.status !== 'applying') {
            headingRow.append(this.renderDismissButton(set));
        }
        card.append(headingRow);

        const list = document.createElement('ul');
        list.className = 'howspace-ai-change-set-list';
        set.operations
            .filter(() => set.status !== 'canceled')
            .filter(operation => set.status !== 'staged' || operation.status === 'pending')
            .forEach(operation => list.append(this.renderOperation(set, operation)));
        if (list.childElementCount > 0) {
            card.append(list);
        }

        const error = this.errors.get(set.id);
        if (error) {
            const notice = document.createElement('p');
            notice.className = 'howspace-ai-change-set-error';
            notice.setAttribute('role', 'alert');
            notice.textContent = error.text;
            card.append(notice);
        }

        if (set.status === 'staged') {
            card.append(this.renderActions(set));
        }
        // An apply that died would otherwise leave the card here for good. The server refuses a retry until the
        // first attempt has had its time.
        if (set.status === 'applying') {
            const actions = document.createElement('div');
            actions.className = 'howspace-ai-change-set-actions';
            actions.append(this.renderButton(this.label('retry'), 'howspace-ai-change-set-apply', () => (
                this.options.actions.apply(set)
            ), set, 'retry'));
            card.append(actions);
        }

        return card;
    }

    private renderOperation(set: PageChangeSetStatusItem, operation: PageChangeOperation): HTMLElement {
        const item = document.createElement('li');
        item.className = `howspace-ai-change-set-item is-${operation.status}`;

        const icon = document.createElement('i');
        icon.className = `fa-light fa-${operation.icon || 'square'} howspace-ai-change-set-icon`;
        icon.setAttribute('aria-hidden', 'true');

        const body = document.createElement('div');
        body.className = 'howspace-ai-change-set-body';
        const title = document.createElement('div');
        title.className = 'howspace-ai-change-set-title';
        title.textContent = operation.title || this.label('untitled');
        body.append(title);

        if (operation.description) {
            const description = document.createElement('p');
            description.className = 'howspace-ai-change-set-description';
            description.textContent = operation.description;
            body.append(description);
        }

        if (operation.items.length > 0) {
            const items = document.createElement('ul');
            items.className = 'howspace-ai-change-set-content';
            operation.items.forEach(text => {
                const entry = document.createElement('li');
                entry.textContent = text;
                items.append(entry);
            });
            body.append(items);
        }

        const outcome = this.outcomeText(operation);
        if (outcome) {
            const outcomeElement = document.createElement('p');
            outcomeElement.className = 'howspace-ai-change-set-outcome';
            outcomeElement.textContent = outcome;
            body.append(outcomeElement);
        }

        item.append(icon, body);
        if (set.status === 'staged' && operation.status === 'pending') {
            const skip = this.renderButton(this.label('skip'), 'howspace-ai-change-set-skip', () => (
                this.options.actions.skip(set, operation.id)
            ), set, `skip-${operation.id}`);
            skip.setAttribute(
                'aria-label',
                this.label('skipNamed').replace('%title%', operation.title || this.label('untitled')),
            );
            item.append(skip);
        }

        return item;
    }

    private renderActions(set: PageChangeSetStatusItem): HTMLElement {
        const actions = document.createElement('div');
        actions.className = 'howspace-ai-change-set-actions';
        const count = pendingCount(set);
        const applyLabel = count === 1
            ? this.label('applyOne')
            : this.label('apply').replace('%count%', String(count));
        actions.append(
            this.renderButton(applyLabel, 'howspace-ai-change-set-apply', () => (
                this.options.actions.apply(set)
            ), set, 'apply'),
            this.renderButton(this.label('cancel'), 'howspace-ai-change-set-cancel', () => (
                this.options.actions.cancel(set)
            ), set, 'cancel'),
        );

        return actions;
    }

    private renderButton(
        text: string,
        className: string,
        onClick: () => Promise<string | null>,
        set: PageChangeSetStatusItem,
        action: string,
    ): HTMLButtonElement {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = className;
        button.textContent = text;
        button.dataset.action = action;
        button.dataset.focusKey = `${action}:${set.id}`;
        // Every button asks the server to act on the set the card shows. While the agent is answering it may
        // still be adding to that set, so nothing is offered until it is done.
        button.disabled = this.busy || this.options.isRunning();
        button.addEventListener('click', () => {
            if (this.busy || this.options.isRunning()) {
                return;
            }
            this.busy = true;
            this.focusAfterAction = button.dataset.focusKey;
            this.errors.delete(set.id);
            this.signature = '';
            this.element.querySelectorAll('button').forEach(element => {
                element.disabled = true;
            });
            const version = versionOf(set);
            void onClick().then(error => {
                this.busy = false;
                if (error) {
                    this.errors.set(set.id, {text: error, version: this.currentVersion(set.id) ?? version});
                }
                this.signature = '';
                this.render(this.statusItems);
            });
        });

        return button;
    }

    private renderDismissButton(set: PageChangeSetStatusItem): HTMLButtonElement {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'howspace-ai-change-set-dismiss';
        button.setAttribute('aria-label', this.options.close || 'Close');
        button.innerHTML = '<i class="fa-light fa-xmark" aria-hidden="true"></i>';
        button.addEventListener('click', () => {
            this.dismissed.add(set.id);
            this.errors.delete(set.id);
            button.closest('.howspace-ai-change-set')?.remove();
            this.element.hidden = this.element.childElementCount === 0;
        });

        return button;
    }

    private headingText(set: PageChangeSetStatusItem): string {
        const page = set.pageTitle || this.label('deletedPage');
        const count = pendingCount(set);
        const key = {
            staged: count === 1 ? 'headingOne' : 'heading',
            applying: 'applying',
            completed: 'completed',
            partly_applied: 'partlyApplied',
            failed: 'failed',
            canceled: 'canceled',
        }[set.status];

        return this.label(key).replace('%count%', String(count)).replace('%page%', page);
    }

    private outcomeText(operation: PageChangeOperation): string {
        const status = {
            applied: this.label('statusApplied'),
            skipped: this.label('statusSkipped'),
            failed: this.label('statusNotApplied'),
            blocked: this.label('statusNotApplied'),
        }[operation.status] || '';
        const reason = operation.reason ? this.label(`reason_${operation.reason}`) : '';

        return [status, reason].filter(Boolean).join('. ');
    }

    private currentVersion(setId: string): string | null {
        const set = this.statusItems.filter(isPageChangeSetStatusItem).find(item => item.id === setId);
        return set ? versionOf(set) : null;
    }

    private label(key: string): string {
        return this.options.labels[key] || '';
    }
}

const versionOf = (set: PageChangeSetStatusItem): string => `${set.status}:${set.revision}`;

const pendingCount = (set: PageChangeSetStatusItem): number => (
    set.operations.filter(operation => operation.status === 'pending').length
);
